import type { ReactNode } from "react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@athena/ui";

interface DeleteDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title?: string;
    deleteTarget?: string | null;
    /**
     * Replaces the generic "are you sure" line when the caller can say more: what exactly goes,
     * and what stays. See "Destructive and batch actions" in `.claude/rules/ui.md`.
     */
    description?: ReactNode;
    /** The verb, repeated — "Delete contact" rather than "Delete". */
    confirmLabel?: string;
    onConfirm: () => void | Promise<void>;
    loading?: boolean;
    /**
     * When set, the deletion can't proceed — shows this explanation instead of the
     * confirm/cancel flow, with a single dismiss button.
     */
    blockedReason?: string | null;
}

export const DeleteDialog = ({
    open,
    onOpenChange,
    title = "Delete",
    deleteTarget,
    description,
    confirmLabel = "Delete",
    onConfirm,
    loading = false,
    blockedReason,
}: DeleteDialogProps) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {blockedReason ? (
                        <DialogDescription>{blockedReason}</DialogDescription>
                    ) : description ? (
                        <DialogDescription>{description}</DialogDescription>
                    ) : (
                        deleteTarget && (
                            <DialogDescription>
                                Are you sure you want to delete <strong>{deleteTarget}</strong>? This cannot be undone.
                            </DialogDescription>
                        )
                    )}
                </DialogHeader>
                <DialogFooter>
                    {blockedReason ? (
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            Got it
                        </Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>
                                Cancel
                            </Button>
                            <Button variant="destructive" disabled={loading} onClick={onConfirm}>
                                {loading ? "Deleting…" : confirmLabel}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
